<template>
  <div class="inline-editor-container">
    <template>
      <v-menu right
              :max-width="fieldType === 'date' ? '500px' : '300px'"
              :min-width="fieldType === 'date' ? '500px' : '300px'"
              :close-on-content-click="false"
              v-model="isEditing">
        <template v-slot:activator="{ on }">
          <div class="d-flex align-center">
            <slot/>
            <v-btn color="kbGreen"
                   class="inline-edit-button"
                   icon
                   small
                   v-if="isEditButtonShown"
                   v-on="on">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </div>
        </template>

        <div class="pt-2 px-3 kbLight"
             style="max-height: 350px; overflow-y: auto"
             :style="{
               height: fieldType === 'phone' && isCountryListOpened ? '270px' : '',
               paddingBottom: (fieldType === 'checkbox' || fieldType === 'radio') && '50px'
             }"
             v-if="isEditing || isEditingFromTable">
          <v-form ref="updatePersonForm" @submit.prevent="onSave">
            <template v-if="fieldType === 'email'">
              <v-text-field color="kbDarkBlueText"
                            autofocus
                            :label="label || $t('general.enterInformation')"
                            :rules="[rules.email, ...inputRules]"
                            v-model="copiedValue"
                            @keypress.enter="onSave()"/>
            </template>

            <template v-else-if="fieldType === 'text'">
              <v-text-field color="kbDarkBlueText"
                            autofocus
                            :hide-details="inputRules === [true]"
                            :label="label || $t('general.enterInformation')"
                            :rules="[...inputRules]"
                            v-model="copiedValue"
                            @keypress.enter="onSave()"/>
            </template>

            <template v-else-if="fieldType === 'money'">
              <v-text-field color="kbDarkBlueText"
                            autofocus
                            :hide-details="inputRules === [true]"
                            :rules="[...inputRules]"
                            v-model.lazy="copiedValue"
                            v-money="money"
                            @keypress.enter="onSave()"/>
            </template>

            <template v-else-if="fieldType === 'numeric'">
              <v-text-field @keypress="checkKeyOnlyNumbers"
                            color="kbDarkBlueText"
                            autofocus
                            :label="label || $t('general.enterInformation')"
                            :hide-details="inputRules === [true]"
                            :rules="[...inputRules]"
                            v-model.lazy="copiedValue"
                            @keypress.enter="onSave()">
              </v-text-field>
            </template>

            <template v-else-if="fieldType === 'phone'">
              <CountryPhonePicker :value="copiedValue"
                                  @close="toggleCountryList(false)"
                                  @input="setCountryPhone"
                                  @open="toggleCountryList(true)"
                                  @keypress.enter="onSave()"/>
            </template>

            <template v-else-if="fieldType === 'textLong'">
              <v-textarea color="kbDarkBlueText"
                          autofocus
                          hide-details
                          :label="label || $t('general.enterInformation')"
                          :rules="[...inputRules]"
                          solo
                          v-model="copiedValue"
                          @keypress.enter="onSave()"/>
            </template>

            <template v-else-if="fieldType === 'select'">
              <v-select autofocus
                        color="kbDarkBlueText"
                        hide-details
                        :items="options"
                        :item-text="optionLabel"
                        :item-value="optionValue"
                        label="Selecciona una opción"
                        ref="select"
                        :rules="[...inputRules]"
                        solo
                        v-model="copiedValue"
                        @change="onSelectValueChanged(true)"
                        @keypress.space="$refs.select.focus()"/>
            </template>

            <template v-else-if="fieldType === 'checkbox'">
              <v-checkbox color="kbDarkBlueText"
                          hide-details
                          :label="optionLabel ? option[optionLabel] : option"
                          :value="optionValue ? option[optionValue] : option"
                          v-model="copiedValue"
                          :multiple="true"
                          v-for="(option, index) in options"
                          :key="`checkbox-${index}`"
                          @keypress.enter="onSave()"/>
            </template>

            <template v-else-if="fieldType === 'radio'">
              <v-radio-group hide-details v-model="copiedValue" @keypress.enter="onSave()">
                <v-radio color="kbDarkBlueText"
                         class="pb-1"
                         :label="optionLabel ? option[optionLabel] : option"
                         :value="optionValue ? option[optionValue] : option"
                         v-for="(option, index) in options"
                         :key="`checkbox-${index}`"/>
              </v-radio-group>
            </template>

            <template v-else-if="fieldType === 'date'">
              <v-date-picker color="kbBlue"
                             full-width
                             reactive
                             v-model="copiedValue"
                             :landscape="$vuetify.breakpoint.mdAndUp">
              </v-date-picker>
            </template>

            <template v-else-if="fieldType === 'dateTime'">
              <DateTimePicker :label="label || $t('general.enterInformation')"
                              v-model="copiedValue">
              </DateTimePicker>
            </template>
          </v-form>

          <div class="kbLight d-flex align-center pt-2 pb-1"
               :class="fieldType === 'date' ? 'justify-space-between' : 'justify-end'"
               :style="
                (fieldType === 'checkbox' || fieldType === 'radio') &&
                'position: fixed; bottom: 0; left: 12px; right: 16px'
               "
          >
            <div v-if="fieldType === 'date'">
              <span class="kbDarkBlueText--text">
                {{ copiedValue | moment('DD/MM/YYYY') }}
              </span>
            </div>
            <div class="d-flex align-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn color="kbRed"
                         class="mr-1"
                         icon
                         @click="toggleMenu(false)"
                         v-on="on">
                    <v-icon size="25px">mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('general.cancel') }}</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn color="kbGreen"
                         icon
                         @click="onSave()"
                         v-on="on">
                    <v-icon size="25px">mdi-checkbox-marked-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('general.edit') }}</span>
              </v-tooltip>
            </div>
          </div>
        </div>
      </v-menu>
    </template>
  </div>
</template>

<script>
import CountryPhonePicker from '@/components/ui/CountryPhonePicker'
import DateTimePicker from '@/components/ui/DateTimePicker'
import rules from '@/libs/rules'
import arraysAndJsonsHelpers from '@/libs/arraysAndJsonsHelpers'
export default {
  components: { CountryPhonePicker, DateTimePicker },
  /* text, textLong, select, radio, checkbox, email, phone */
  created () {
    document.addEventListener('keydown', e => this.handleKeyDown(e))
  },
  data () {
    return {
      copiedValue: '',
      selectValueChanged: false,
      isCountryListOpened: false,
      isEditing: false,
      money: {
        decimal: ',',
        thousands: '.',
        prefix: '$ ',
        suffix: '',
        precision: 2,
        masked: true /* doesn't work with directive */
      },
      rules
    }
  },
  destroyed () {
    document.removeEventListener('keydown', e => this.handleKeyDown(e))
  },
  methods: {
    checkKeyOnlyNumbers (event) {
      if (event.keyCode < 48 || event.keyCode > 58) {
        event.preventDefault()
      }
    },
    toggleMenu (value) {
      this.isEditing = value
      if (!value) {
        this.onSelectValueChanged(false)
        this.$emit('menuClosed')
      }
    },
    handleKeyDown (e) {
      if (!this.isEditing && !this.isEditingFromTable) return
      if (e.key !== ' ' && e.key !== 'Enter' && e.key !== 'Escape') return
      if (e.key === 'Escape') return this.toggleMenu(false)
      if (e.key === 'Enter' &&
         this.fieldType === 'select' &&
         this.$refs.select &&
         this.copiedValue &&
         this.selectValueChanged) {
        this.onSave()
      }
    },
    onSave () {
      if (this.$refs.updatePersonForm && !this.$refs.updatePersonForm.validate()) return
      if (this.fieldType === 'phone' && (!this.copiedValue.valid && this.copiedValue.phone)) return
      this.$emit('onSave', this.copiedValue)
      this.toggleMenu(false)
    },
    onSelectValueChanged (value) {
      this.selectValueChanged = value
    },
    setCheckboxModelingVariable () {
      let checkboxVariable
      if (!this.value) {
        checkboxVariable = []
      } else if (Array.isArray(this.value)) {
        checkboxVariable = [...this.value]
      } else if (typeof this.value === 'string' && arraysAndJsonsHelpers.isJson(this.value)) {
        checkboxVariable = JSON.parse(this.value)
      }
      this.copiedValue = checkboxVariable
    },
    setCountryPhone (data) {
      this.copiedValue = { ...data }
    },
    setDateModelingVariable () {
      if (!Date.parse(this.value)) {
        this.copiedValue = ''
      } else {
        this.copiedValue = this.value
      }
    },
    setModelingVariable () {
      if (!this.value && this.fieldType !== 'phone' && this.fieldType !== 'checkbox') {
        this.copiedValue = ''
      } else if (this.fieldType === 'phone') {
        this.setPhoneModelingVariable()
      } else if (this.fieldType === 'checkbox') {
        this.setCheckboxModelingVariable()
      } else if (this.fieldType === 'date') {
        this.setDateModelingVariable()
      } else {
        this.copiedValue = this.value
      }
    },
    setPhoneModelingVariable () {
      let phoneObject
      if (!this.value || (typeof this.value === 'object' && !Object.keys(this.value).length)) {
        phoneObject = {
          country: 'Colombia',
          code: 'co',
          number: '57',
          phone: '',
          valid: true
        }
      } else if (typeof this.value === 'string' && arraysAndJsonsHelpers.isJson(this.value)) {
        phoneObject = JSON.parse(this.value)
      } else {
        let { code, country = '', number, phone, valid = true } = this.value
        if (!code) {
          code = this.value[this.countryCodeKey]
        }
        if (!number) {
          number = this.value[this.numberKey]
        }
        country = country.toLowerCase()
        phoneObject = { code, country, number, phone, valid }
      }
      this.setCountryPhone(phoneObject)
    },
    toggleCountryList (value) {
      this.isCountryListOpened = value
    },
    unsetModelingVariable () {
      this.copiedValue = ''
    }
  },
  props: {
    fieldType: {
      type: String,
      default: 'text'
    },
    isEditButtonShown: {
      type: Boolean,
      default: true
    },
    isEditingFromTable: {
      type: Boolean,
      default: false
    },
    isEditingOpen: {
      type: Boolean,
      default: false
    },
    label: {
      type: String
    },
    optionLabel: {
      type: String,
      default: ''
    },
    optionValue: {
      type: String,
      default: ''
    },
    options: {
      type: [Array, Object],
      default: () => []
    },
    numberKey: {
      type: String,
      default: 'number'
    },
    countryCodeKey: {
      type: String,
      default: 'code'
    },
    inputRules: {
      type: Array,
      default: () => [true]
    },
    value: {
      type: [String, Array, Object]
    }
  },
  watch: {
    isEditing (newValue) {
      if (!newValue) {
        this.unsetModelingVariable()
        this.toggleCountryList(false)
        this.$emit('menuClosed')
      } else {
        this.setModelingVariable()
      }
    },
    isEditingFromTable (value) {
      if (value) this.toggleMenu(true)
    },
    isEditingOpen (newValue) {
      this.isEditing = newValue
    }
  }
}
</script>

<style scoped>
  .inline-edit-button {
    filter: opacity(0);
    transition: all ease-in-out 0.3s;
  }
  .inline-editor-container:hover .inline-edit-button {
    filter: opacity(1);
  }
</style>
